// The in-memory API's KPI visibility (KPI-VISIBILITY fake twin, founder-approved 2026-10-22; 05
// §KPI visibility): the grants (`GET/POST kpi-visibility/grants`, `PATCH/DELETE …/{id}`, `GET
// kpi-visibility/preview`) at parity with services/api/src/routes/org/kpi-visibility.ts, and the
// caller's standing every twin KPI read shares (`standingOf`): the same pure resolver as the API
// (`@companyos/domain-work/org` kpi-visibility) over the structure twin's seats, holders and
// teams. Routes are a company manager's (403 otherwise). A Northwind twin would start with the
// founder's example on its Account Manager role (Same level + Up my chain, all KPI dimensions),
// as seed-demo gives it; Acme starts with none, so the chart's existing audiences hold.
import {
  grantAnchors,
  grantedKpiVisibility,
  KPI_GROUPS,
  KPI_SCOPES,
  type KpiGrant,
  type KpiGroup,
  type KpiTree,
  resolveKpiVisibility,
  seesKpi,
} from "@companyos/domain-work/org";
import type { OperationOutput, Problem, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import type { FakeCompany } from "./fake-data";
import type { Person, Seat, SeatAssignment } from "./types";

type Grant = OperationOutput<"kpiVisibility.list">["items"][number];
type Preview = OperationOutput<"kpiVisibility.preview">;
type TeamRow = {
  id: string;
  name: string;
  lead_seat_id: string | null;
  human_owner_seat_id: string | null;
};

export type KpiVisibilityDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  newId: (n: number) => string;
  seatsOf: (company: FakeCompany) => Seat[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  teamsOf: (company: FakeCompany) => TeamRow[];
  teamMembersOf: (company: FakeCompany) => { team_id: string; seat_id: string }[];
  rolesOf: (companyId: string) => { id: string; name: string }[];
  peopleOf: (p: Principal) => Person[];
  /** The signed-in demo accounts' memberships of the company (the twin lists them apart). */
  accountsOf: (p: Principal) => { id: string; display_name: string; roles: string[] }[];
};

export type KpiStanding = {
  all: (group: KpiGroup) => boolean;
  sees: (group: KpiGroup, seatId: string) => boolean;
  grants_reach: (group: KpiGroup, seatId: string) => boolean;
};

/** The job role each demo company's founder-example grant names. */
const SEEDED: Record<string, string> = { "demo-recruiting": "Account Manager" };

export function createKpiVisibility(d: KpiVisibilityDeps) {
  /** Everyone with a membership: the listed people and the demo accounts. */
  const peopleOf = (p: Principal) => {
    const listed: { id: string; display_name: string; roles: readonly string[] }[] = d.peopleOf(p);
    const ids = new Set(listed.map((x) => x.id));
    return [...listed, ...d.accountsOf(p).filter((a) => !ids.has(a.id))];
  };
  const store = new Map<string, Grant[]>();
  let n = 0;

  function treeOf(company: FakeCompany): KpiTree {
    const live = d.assignmentsOf(company).filter((a) => a.active_to === null);
    const seats = d
      .seatsOf(company)
      .filter((s) => s.status !== "proposed")
      .map((s) => ({
        id: s.id,
        supervisor_seat_id: s.supervisor_seat_id,
        role_id: s.role_id,
        holders: live.filter((a) => a.seat_id === s.id).map((a) => a.membership_id),
      }));
    const teams = new Map<string, string[]>();
    const add = (team: string, seat: string | null) => {
      if (!seat) return;
      const list = teams.get(team) ?? [];
      if (!list.includes(seat)) list.push(seat);
      teams.set(team, list);
    };
    for (const m of d.teamMembersOf(company)) add(m.team_id, m.seat_id);
    for (const t of d.teamsOf(company)) {
      add(t.id, t.lead_seat_id);
      add(t.id, t.human_owner_seat_id);
    }
    return { seats, teams };
  }

  function nameOf(p: Principal, kind: Grant["principal_kind"], id: string): string | null {
    switch (kind) {
      case "role":
        return d.rolesOf(p.company.id).find((r) => r.id === id)?.name ?? null;
      case "seat":
        return d.seatsOf(p.company).find((s) => s.id === id)?.title ?? null;
      case "team":
        return d.teamsOf(p.company).find((t) => t.id === id)?.name ?? null;
      case "person":
        return peopleOf(p).find((x) => x.id === id)?.display_name ?? null;
    }
  }

  /** The company's grants (Northwind's founder example seeded on first read). */
  function grantsOf(p: Principal): Grant[] {
    let list = store.get(p.company.id);
    if (!list) {
      list = [];
      store.set(p.company.id, list);
      const am = d.rolesOf(p.company.id).find((r) => r.name === SEEDED[p.company.slug]);
      if (am) {
        const at = d.now().toISOString();
        list.push({
          id: d.newId(++n),
          principal_kind: "role",
          principal_id: am.id,
          principal_name: am.name,
          scopes: ["peers", "up"],
          groups: [],
          created_at: at,
          updated_at: at,
        });
      }
    }
    return list;
  }

  const asGrant = (g: Grant): KpiGrant => ({
    id: g.id,
    principal_kind: g.principal_kind,
    principal_id: g.principal_id,
    scopes: g.scopes,
    groups: g.groups,
  });

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? "";

  /** A person's standing: default plus grants, and the grants alone. */
  function standingFor(p: Principal, membershipId: string, manager: boolean): KpiStanding {
    const tree = treeOf(p.company);
    const grants = grantsOf(p).map(asGrant);
    const viewer = { membership_id: membershipId, manager };
    const visible = resolveKpiVisibility(tree, viewer, grants);
    const granted = grantedKpiVisibility(tree, viewer, grants);
    return {
      all: (g) => visible[g].all,
      sees: (g, seat) => seesKpi(visible, g, seat),
      grants_reach: (g, seat) => seesKpi(granted, g, seat),
    };
  }

  /** The caller's standing (the API's `kpiVisibilityOf`). */
  const standingOf = (p: Principal): KpiStanding => standingFor(p, membershipOf(p), p.manager);

  const forbidden = () =>
    d.problem(403, "forbidden", "Forbidden", "KPI visibility is set by a company manager");
  const scopesOk = (v: unknown) =>
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((x) => (KPI_SCOPES as readonly unknown[]).includes(x));
  const groupsOk = (v: unknown) =>
    v === undefined ||
    (Array.isArray(v) && v.every((x) => (KPI_GROUPS as readonly unknown[]).includes(x)));
  const ordered = <T extends string>(all: readonly T[], picked: readonly T[]) =>
    all.filter((x) => picked.includes(x));

  const handlers: Partial<Record<RouteId, Handler>> = {
    "kpiVisibility.list": ({ principal: p }) => {
      if (!p) return d.notFound();
      if (!p.manager) return forbidden();
      return d.json({
        items: grantsOf(p).map((g) => ({
          ...g,
          principal_name: nameOf(p, g.principal_kind, g.principal_id),
        })),
      });
    },
    "kpiVisibility.create": ({ principal: p, body }) => {
      if (!p) return d.notFound();
      if (!p.manager) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      const kind = b.principal_kind as Grant["principal_kind"];
      if (!["role", "seat", "team", "person"].includes(kind))
        return d.validation("principal_kind: role, seat, team or person");
      if (!scopesOk(b.scopes)) return d.validation("scopes: at least one known scope");
      if (!groupsOk(b.groups)) return d.validation("groups: known groups only");
      const id = String(b.principal_id ?? "");
      const name = nameOf(p, kind, id);
      if (name === null) return d.validation(`principal_id: no such ${kind} in this company`);
      const at = d.now().toISOString();
      const g: Grant = {
        id: d.newId(++n),
        principal_kind: kind,
        principal_id: id,
        principal_name: name,
        scopes: ordered(KPI_SCOPES, b.scopes as Grant["scopes"]),
        groups: ordered(KPI_GROUPS, (b.groups ?? []) as Grant["groups"]),
        created_at: at,
        updated_at: at,
      };
      grantsOf(p).push(g);
      return d.json(g, 201);
    },
    "kpiVisibility.update": ({ principal: p, params, body }) => {
      if (!p) return d.notFound();
      if (!p.manager) return forbidden();
      const g = grantsOf(p).find((x) => x.id === params.id);
      if (!g) return d.notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.scopes !== undefined && !scopesOk(b.scopes))
        return d.validation("scopes: at least one known scope");
      if (!groupsOk(b.groups)) return d.validation("groups: known groups only");
      if (b.scopes) g.scopes = ordered(KPI_SCOPES, b.scopes as Grant["scopes"]);
      if (b.groups) g.groups = ordered(KPI_GROUPS, b.groups as Grant["groups"]);
      g.updated_at = d.now().toISOString();
      return d.json({ ...g, principal_name: nameOf(p, g.principal_kind, g.principal_id) });
    },
    "kpiVisibility.delete": ({ principal: p, params }) => {
      if (!p) return d.notFound();
      if (!p.manager) return forbidden();
      const list = grantsOf(p);
      const i = list.findIndex((x) => x.id === params.id);
      if (i < 0) return d.notFound();
      list.splice(i, 1);
      return new Response(null, { status: 204 });
    },
    "kpiVisibility.preview": ({ principal: p, url }) => {
      if (!p) return d.notFound();
      if (!p.manager) return forbidden();
      const id = url.searchParams.get("membership_id") ?? "";
      // A membership of the company: a listed person, a demo account, or a seat holder (a DE).
      const person = peopleOf(p).find((x) => x.id === id);
      const tree = treeOf(p.company);
      if (!person && !tree.seats.some((x) => x.holders.includes(id))) return d.notFound();
      const manager = (person?.roles ?? []).some((r) => r === "owner" || r === "admin");
      const s = standingFor(p, id, manager);
      const held = tree.seats.filter((x) => x.holders.includes(id)).map((x) => x.id);
      const heldSet = new Set(held);
      const groups = {} as Preview["groups"];
      for (const g of KPI_GROUPS)
        groups[g] = {
          all: s.all(g),
          seat_ids: tree.seats.filter((x) => s.sees(g, x.id)).map((x) => x.id),
        };
      const out: Preview = {
        membership_id: id,
        manager,
        held_seat_ids: held,
        groups,
        grant_ids: grantsOf(p)
          .filter(
            (g) =>
              grantAnchors(asGrant(g), tree, heldSet).length > 0 ||
              (g.principal_kind === "person" && g.principal_id === id),
          )
          .map((g) => g.id),
      };
      return d.json(out);
    },
  };

  return { handlers, standingOf };
}
